import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MdQuiz,
  MdAdd,
  MdDelete,
  MdVisibility,
  MdListAlt,
  MdSearch,
  MdEdit,
} from 'react-icons/md';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import './Assessment.css';

const AssessmentsCreated = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('own');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [progressFilter, setProgressFilter] = useState('');
  const [creatorFilter, setCreatorFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/assessments/created');
      setItems(data.data || []);
      setScope(data.scope || (isSuperAdmin ? 'all' : 'own'));
    } catch {
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this assessment and all attempts?')) return;
    try {
      await api.delete(`/assessments/${id}`);
      toast.success('Assessment deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const showAll = scope === 'all' || isSuperAdmin;

  const creators = useMemo(() => {
    if (!showAll) return [];
    const map = new Map();
    items.forEach((a) => {
      const id = a.createdBy?._id || a.createdBy;
      if (!id) return;
      const name = a.createdBy?.name || 'Unknown';
      map.set(String(id), name);
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, showAll]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;

    return items.filter((a) => {
      if (statusFilter && (a.status || 'published') !== statusFilter) {
        return false;
      }

      if (showAll && creatorFilter) {
        const cid = String(a.createdBy?._id || a.createdBy || '');
        if (cid !== creatorFilter) return false;
      }

      if (from || to) {
        const created = a.createdAt ? new Date(a.createdAt) : null;
        if (!created || Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
      }

      const stats = a.attemptStats || { total: 0, submitted: 0, evaluated: 0 };
      if (!showAll) {
        if (progressFilter === 'no_attempts' && stats.total > 0) return false;
        if (progressFilter === 'awaiting_submissions') {
          if (stats.total === 0 || stats.submitted > 0) return false;
        }
        if (progressFilter === 'pending_review') {
          if (stats.submitted <= stats.evaluated) return false;
        }
        if (progressFilter === 'fully_evaluated') {
          if (stats.total === 0 || stats.evaluated < stats.total) return false;
        }
        if (progressFilter === 'has_submissions' && stats.submitted === 0) {
          return false;
        }
      }

      if (!q) return true;
      const hay = [
        a.title,
        a.description,
        a.createdBy?.name,
        a.createdBy?.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [
    items,
    search,
    statusFilter,
    progressFilter,
    creatorFilter,
    dateFrom,
    dateTo,
    showAll,
  ]);

  const hasActiveFilters =
    Boolean(search.trim()) ||
    Boolean(statusFilter) ||
    Boolean(progressFilter) ||
    Boolean(creatorFilter) ||
    Boolean(dateFrom) ||
    Boolean(dateTo);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setProgressFilter('');
    setCreatorFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const canManage = (a) => {
    if (isSuperAdmin) return true;
    const creatorId = String(a.createdBy?._id || a.createdBy || '');
    return creatorId === String(user?.id || user?._id || '');
  };

  return (
    <div className="assessment-page">
      <div className="assessment-header">
        <div>
          <h1>
            <MdQuiz size={28} />
            {showAll ? 'All Assessments' : 'Assessments I Created'}
          </h1>
          <p className="assessment-subtitle">
            {showAll
              ? 'Super Admin view: every assessment in the system. Search and filter across all creators.'
              : 'Only assessments you created appear here. Search and filter your own assessments.'}
          </p>
        </div>
        <div className="assessment-actions">
          <Link to="/assessments" className="assessment-btn secondary">
            Assigned
          </Link>
          <Link to="/assessments/new" className="assessment-btn">
            <MdAdd size={18} />
            Create assessment
          </Link>
        </div>
      </div>

      {!loading && items.length > 0 && (
        <>
          <div className="assessment-filters-bar">
            <div className="assessment-search-wrap">
              <MdSearch size={20} />
              <input
                type="search"
                placeholder={
                  showAll
                    ? 'Search title, creator, description…'
                    : 'Search by title or description…'
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search assessments"
              />
            </div>
            <select
              className="form-control"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="published">Published</option>
              <option value="closed">Closed</option>
            </select>
            {!showAll && (
              <select
                className="form-control"
                value={progressFilter}
                onChange={(e) => setProgressFilter(e.target.value)}
                aria-label="Filter by progress"
              >
                <option value="">All progress</option>
                <option value="no_attempts">No attempts yet</option>
                <option value="awaiting_submissions">Awaiting submissions</option>
                <option value="has_submissions">Has submissions</option>
                <option value="pending_review">Pending review</option>
                <option value="fully_evaluated">Fully evaluated</option>
              </select>
            )}
            {showAll && creators.length > 0 && (
              <select
                className="form-control"
                value={creatorFilter}
                onChange={(e) => setCreatorFilter(e.target.value)}
                aria-label="Filter by creator"
              >
                <option value="">All creators</option>
                {creators.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
            <div className="assessment-date-range">
              <label htmlFor="assessment-date-from">From</label>
              <input
                id="assessment-date-from"
                type="date"
                className="form-control"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
                aria-label="Created from date"
              />
              <label htmlFor="assessment-date-to">To</label>
              <input
                id="assessment-date-to"
                type="date"
                className="form-control"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                aria-label="Created to date"
              />
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                className="assessment-btn secondary"
                onClick={clearFilters}
              >
                Clear
              </button>
            )}
          </div>
          <p className="assessment-filter-meta">
            Showing {filtered.length} of {items.length} assessment
            {items.length === 1 ? '' : 's'}
            {showAll ? ' (system-wide)' : ' (yours)'}
          </p>
        </>
      )}

      {loading ? (
        <div className="assessment-loading">
          <span className="spinner" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="assessment-empty">
          {showAll
            ? 'No assessments have been created yet.'
            : 'You have not created any assessments yet.'}{' '}
          <Link to="/assessments/new">Create one</Link>
        </div>
      ) : filtered.length === 0 ? (
        <div className="assessment-empty">
          No assessments match your search or filters.{' '}
          <button
            type="button"
            className="assessment-btn secondary"
            onClick={clearFilters}
            style={{ marginLeft: 8 }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="card assessment-table-card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  {showAll && <th>Created by</th>}
                  <th>Status</th>
                  <th>Date &amp; time</th>
                  <th>Duration</th>
                  <th>Marks</th>
                  <th>Assigned</th>
                  <th>Submitted</th>
                  <th>Evaluated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a._id}>
                    <td>
                      <div className="assessment-table-title">{a.title}</div>
                      {a.description ? (
                        <div className="assessment-table-sub">
                          {a.description}
                        </div>
                      ) : null}
                    </td>
                    {showAll && (
                      <td>{a.createdBy?.name || '—'}</td>
                    )}
                    <td>
                      <span className="assessment-badge">
                        {a.mode === 'public' ? 'public' : 'private'}
                      </span>{' '}
                      <span className="assessment-badge">
                        {a.status || 'published'}
                      </span>
                    </td>
                    <td>
                      {a.scheduledAt
                        ? new Date(a.scheduledAt).toLocaleString(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })
                        : '—'}
                    </td>
                    <td>{a.durationMinutes} min</td>
                    <td>{a.totalMarks}</td>
                    <td>
                      {a.mode === 'public'
                        ? (a.publicEmails || []).length
                        : (a.assignedTo || []).length}
                    </td>
                    <td>
                      {a.attemptStats?.submitted || 0}/
                      {a.attemptStats?.total || 0}
                    </td>
                    <td>{a.attemptStats?.evaluated || 0}</td>
                    <td>
                      {canManage(a) ? (
                        <div className="assessment-table-actions">
                          {a.editable && (
                            <button
                              type="button"
                              className="assessment-btn secondary sm icon-only"
                              title="Edit assessment"
                              aria-label="Edit assessment"
                              onClick={() =>
                                navigate(`/assessments/${a._id}/edit`)
                              }
                            >
                              <MdEdit size={18} />
                            </button>
                          )}
                          <button
                            type="button"
                            className="assessment-btn secondary sm icon-only"
                            title="View assessment"
                            aria-label="View assessment"
                            onClick={() =>
                              navigate(`/assessments/${a._id}/view`)
                            }
                          >
                            <MdListAlt size={18} />
                          </button>
                          <button
                            type="button"
                            className="assessment-btn sm icon-only"
                            title="Review"
                            aria-label="Review"
                            onClick={() =>
                              navigate(`/assessments/${a._id}/review`)
                            }
                          >
                            <MdVisibility size={18} />
                          </button>
                          <button
                            type="button"
                            className="assessment-btn danger sm icon-only"
                            title="Delete"
                            aria-label="Delete"
                            onClick={() => handleDelete(a._id)}
                          >
                            <MdDelete size={18} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentsCreated;
